// Export COMPLETO de movimientos (pedido Bramarket, 10-oct-2026): compras, canjes, pagos con saldo,
// ajustes y devoluciones con monto, cashback, folio, sucursal y quién atendió, en hora del negocio.
// El archivo lo arma el servidor (GET /loyalty/movements.xlsx): sin tope de filas, rango máx. 366 días.
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FileSpreadsheet, Loader2, MapPin, X } from 'lucide-react';
import { clientsApi } from '../../../api/clientsApi';

interface Props {
  timeZone: string;
  branches: { id: string | number; name: string }[];
  onClose: () => void;
}

const ymd = (d: Date, tz: string) => new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(d);
const sumaDias = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

const ExportMovementsModal: React.FC<Props> = ({ timeZone, branches, onClose }) => {
  const hoy = useMemo(() => ymd(new Date(), timeZone), [timeZone]);
  const atajos = useMemo(() => {
    const ini = `${hoy.slice(0, 8)}01`;
    const finPasado = sumaDias(ini, -1);
    return [
      { k: 'hoy', label: 'Hoy', from: hoy, to: hoy },
      { k: 'ayer', label: 'Ayer', from: sumaDias(hoy, -1), to: sumaDias(hoy, -1) },
      { k: '7', label: 'Últimos 7 días', from: sumaDias(hoy, -6), to: hoy },
      { k: 'mes', label: 'Este mes', from: ini, to: hoy },
      { k: 'pasado', label: 'Mes pasado', from: `${finPasado.slice(0, 8)}01`, to: finPasado },
    ];
  }, [hoy]);
  const [from, setFrom] = useState(atajos[3].from);
  const [to, setTo] = useState(atajos[3].to);
  const [branchId, setBranchId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const descargar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!from || !to) return;
    setBusy(true); setError('');
    try {
      await clientsApi.exportMovements({ from, to, branchId: branchId || undefined });
      onClose();
    } catch (err: any) {
      setError(err?.message || 'No se pudo generar el Excel');
    } finally {
      setBusy(false);
    }
  };

  const input = 'w-full rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-sm text-content outline-none focus:border-accent/50';

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={descargar} data-testid="export-movimientos"
        className="modal-sheet w-full rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-md sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content">
            <FileSpreadsheet size={18} className="text-accent" /> Exportar movimientos
          </h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>
        <p className="mb-4 text-sm text-content-2">
          Compras, canjes, pagos con saldo y ajustes, con monto, folio, sucursal y quién atendió. Hora local del negocio.
        </p>

        <div className="mb-3 flex flex-wrap gap-1.5">
          {atajos.map((a) => {
            const activo = a.from === from && a.to === to;
            return (
              <button key={a.k} type="button" onClick={() => { setFrom(a.from); setTo(a.to); }}
                className={`rounded-full border px-3 py-1 text-xs ${activo ? 'border-accent bg-accent/10 text-accent' : 'border-line text-content-2 hover:bg-surface-2'}`}>
                {a.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">Desde</label>
            <input type="date" value={from} max={to || hoy} onChange={(e) => setFrom(e.target.value)} required className={input} data-testid="export-desde" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-content-2">Hasta</label>
            <input type="date" value={to} min={from} max={hoy} onChange={(e) => setTo(e.target.value)} required className={input} data-testid="export-hasta" />
          </div>
        </div>

        {branches.length >= 2 && (
          <div className="mt-3">
            <label className="mb-1 flex items-center gap-1 text-xs font-medium text-content-2"><MapPin size={12} /> Sucursal</label>
            <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={input} data-testid="export-sucursal">
              <option value="">Todas</option>
              {branches.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
            </select>
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400" data-testid="export-error">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
          <button type="submit" disabled={busy || !from || !to}
            className="inline-flex items-center gap-2 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-white hover:bg-accent-solid/90 disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />} Descargar Excel
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default ExportMovementsModal;
